import { Suspense, useEffect, useRef, useState, lazy } from 'react';
import useReveals from './hooks/useReveals.js';
import useSkyline from './hooks/useSkyline.js';
import Nav from './components/Nav.jsx';
import Hero from './components/Hero.jsx';
import Interlude from './components/Interlude.jsx';
import Events from './components/Events.jsx';
import Connections from './components/Connections.jsx';
import StartingKits from './components/StartingKits.jsx';
import Fee from './components/Fee.jsx';
import BriefForm from './components/BriefForm.jsx';
import Footer from './components/Footer.jsx';

const HiddenGemMap = lazy(() => import('./components/HiddenGemMap.jsx'));
const NeighborhoodExplorerMap = lazy(() => import('./components/NeighborhoodExplorerMap.jsx'));
const SecuredDossier = lazy(() => import('./components/SecuredDossier.jsx'));

// Hash routes that swap the full-screen experience in for the landing page.
const ROUTES = {
  '#/hidden-gem-map': 'gem-map',
  '#/neighborhood-explorer': 'neighborhood',
  '#/secured': 'secured',
};

const MapFallback = () => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0a0a0b',
      color: '#c9a86a',
      font: "400 14px/1 'JetBrains Mono', monospace",
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    }}
  >
    Loading the map…
  </div>
);

const routeForHash = () => ROUTES[window.location.hash] || 'home';

function Home() {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  useSkyline(canvasRef);
  useReveals(rootRef);

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', width: '100%', overflowX: 'hidden', background: '#0a0a0b' }}
    >
      <div ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background:
            'radial-gradient(125% 95% at 50% 24%, rgba(10,10,11,0) 26%, rgba(10,10,11,0.42) 66%, rgba(10,10,11,0.92) 100%)',
        }}
      />

      <Nav />
      <Hero />
      <Interlude />
      <StartingKits />
      <Events />
      <Connections />
      <Fee />
      <BriefForm />
      <Footer />
    </div>
  );
}

export default function App() {
  const [view, setView] = useState(routeForHash);

  useEffect(() => {
    const onHashChange = () => setView(routeForHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const goHome = () => {
    window.location.hash = '';
  };

  if (view === 'gem-map') {
    return (
      <Suspense fallback={<MapFallback />}>
        <HiddenGemMap onBack={goHome} />
      </Suspense>
    );
  }

  if (view === 'neighborhood') {
    return (
      <Suspense fallback={<MapFallback />}>
        <NeighborhoodExplorerMap onBack={goHome} />
      </Suspense>
    );
  }

  if (view === 'secured') {
    return (
      <Suspense fallback={<MapFallback />}>
        <SecuredDossier onGoHome={goHome} />
      </Suspense>
    );
  }

  return <Home />;
}
